import React, { useCallback, useEffect, useState } from 'react';
import {
  Calendar,
  Check,
  Copy,
  Download,
  ExternalLink,
  EyeOff,
  Play,
  QrCode,
  Radio,
  RefreshCw,
  Tv,
  Users,
  X,
} from 'lucide-react';
import { FORMAT_PRESETS, type ClientGameView, type FormatId, type GameIntent } from '#shared';
import {
  getSessionBaseUrl,
  playtableApi,
  type AccountProfileDTO,
  type ScheduledGameItemDTO,
} from '../lib/clientApi.ts';

// ============================================================================
// 1. PUBLIC STREAMED GAMES CALENDAR MODAL
// ============================================================================

export function StreamedCalendarModal({
  profile,
  ensureSignedIn,
  onJoinLaunchedTable,
  onClose,
}: {
  profile: AccountProfileDTO | null;
  ensureSignedIn: () => Promise<AccountProfileDTO | null>;
  onJoinLaunchedTable: (tableId: string, overlaySecret?: string) => void;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'browse' | 'schedule'>('browse');
  const [games, setGames] = useState<ScheduledGameItemDTO[]>([]);
  const [filterFormat, setFilterFormat] = useState<FormatId | ''>('');
  const [streamedOnly, setStreamedOnly] = useState(false);
  const [openOnly, setOpenOnly] = useState(false);
  const [commanderInputByEvent, setCommanderInputByEvent] = useState<Record<string, string>>({});
  const [consentByEvent, setConsentByEvent] = useState<Record<string, boolean>>({});
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  // Create form state
  const [title, setTitle] = useState('Friday Night Streamed Commander Pod');
  const [description, setDescription] = useState('Bracket 3 casual pod — bring fun brews!');
  const [format, setFormat] = useState<FormatId>('commander');
  const [bracket, setBracket] = useState<string>('3');
  const [maxSeats, setMaxSeats] = useState<number>(4);
  const [startsLocal, setStartsLocal] = useState<string>(() => {
    const d = new Date(Date.now() + 2 * 60 * 60 * 1000);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
      d.getHours()
    )}:00`;
  });
  const [durationMin, setDurationMin] = useState<number>(120);
  const [recurrence, setRecurrence] = useState<'none' | 'weekly' | 'biweekly'>('none');
  const [approvalMode, setApprovalMode] = useState<'first_come' | 'host_approved'>('first_come');
  const [isStreamed, setIsStreamed] = useState<boolean>(true);
  const [streamUrl, setStreamUrl] = useState<string>('https://twitch.tv/deckstr');

  const refreshCalendar = useCallback(async () => {
    try {
      const res = await playtableApi.listScheduledGames({
        format: filterFormat,
        streamedOnly,
        openSlotsOnly: openOnly,
      });
      setGames(res.games);
    } catch (err: any) {
      setStatusMsg(err.message);
    }
  }, [filterFormat, streamedOnly, openOnly]);

  useEffect(() => {
    refreshCalendar();
  }, [refreshCalendar]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const handleScheduleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const acct = await ensureSignedIn();
      if (!acct) return;
      const startsAt = new Date(startsLocal).getTime();
      await playtableApi.createScheduledGame({
        title,
        description,
        format,
        bracket: bracket ? Number(bracket) : null,
        maxSeats,
        startsAt,
        durationMin,
        recurrence,
        approvalMode,
        isStreamed,
        streamUrl: isStreamed && streamUrl.trim() ? streamUrl.trim() : null,
      });
      setTab('browse');
      setStatusMsg('Scheduled game published to the public calendar!');
      refreshCalendar();
    } catch (err: any) {
      setStatusMsg(err.message);
    }
  };

  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Streamed Games Calendar"
      onClick={onClose}
      className="fixed inset-0 z-[105] bg-black/80 backdrop-blur-sm flex items-center justify-center p-3 overscroll-contain"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-4xl max-h-[90vh] rounded-2xl bg-slate-950 border border-slate-800 shadow-2xl flex flex-col overflow-hidden overscroll-contain"
      >
        <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-violet-400" />
            <h2 className="font-extrabold text-sm text-slate-100">
              Public & Streamed Games Calendar ({localTz})
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close calendar modal"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto overscroll-contain space-y-4 text-xs text-slate-200">
          <div className="flex items-center justify-between gap-2">
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setTab('browse')}
                className={`px-3 py-1.5 rounded-xl font-bold ${
                  tab === 'browse'
                    ? 'bg-violet-600 text-white'
                    : 'bg-slate-900 border border-slate-800 text-slate-300'
                }`}
              >
                Upcoming Pods ({games.length})
              </button>
              <button
                type="button"
                onClick={() => setTab('schedule')}
                className={`px-3 py-1.5 rounded-xl font-bold ${
                  tab === 'schedule'
                    ? 'bg-amber-500 text-slate-950'
                    : 'bg-slate-900 border border-slate-800 text-slate-300'
                }`}
              >
                + Schedule a Pod / Stream
              </button>
            </div>
            <button
              type="button"
              onClick={refreshCalendar}
              className="px-2.5 py-1 rounded-lg bg-slate-900 hover:bg-slate-800 border border-slate-800 text-slate-300 flex items-center gap-1"
            >
              <RefreshCw className="w-3 h-3" />
              <span>Refresh</span>
            </button>
          </div>

          {statusMsg && (
            <div className="px-3 py-2 rounded-xl bg-indigo-950/75 border border-indigo-400/50 text-indigo-200 font-semibold flex items-center justify-between">
              <span>{statusMsg}</span>
              <button
                type="button"
                onClick={() => setStatusMsg(null)}
                className="text-slate-400 hover:text-white"
              >
                ✕
              </button>
            </div>
          )}

          {tab === 'browse' ? (
            <div className="space-y-3">
              {/* Filter Bar */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select
                  value={filterFormat}
                  onChange={(e) => setFilterFormat(e.target.value as FormatId | '')}
                  className="px-2.5 py-1.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-200"
                >
                  <option value="">All Formats</option>
                  {Object.values(FORMAT_PRESETS).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <label className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 cursor-pointer">
                  <span>Streamed games only</span>
                  <input
                    type="checkbox"
                    checked={streamedOnly}
                    onChange={(e) => setStreamedOnly(e.target.checked)}
                    className="accent-rose-500"
                  />
                </label>
                <label className="flex items-center justify-between px-3 py-1.5 rounded-xl bg-slate-900 border border-slate-800 cursor-pointer">
                  <span>Open player slots</span>
                  <input
                    type="checkbox"
                    checked={openOnly}
                    onChange={(e) => setOpenOnly(e.target.checked)}
                    className="accent-emerald-500"
                  />
                </label>
              </div>

              {games.length === 0 ? (
                <div className="py-10 text-center text-slate-400 rounded-2xl border border-dashed border-slate-800 space-y-2">
                  <Tv className="w-6 h-6 text-violet-400 mx-auto" />
                  <div className="font-bold text-slate-200">No scheduled pods match your filter</div>
                  <p className="text-[11px] text-slate-500">
                    Schedule a community Commander night, cEDH league pod, or Twitch streamed showcase!
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {games.map((g) => {
                    const isHost = Boolean(profile && profile.publicId === g.hostPublicId);
                    const mySignup = profile
                      ? g.signups.find((s) => s.publicId === profile.publicId)
                      : null;
                    const localTimeStr = new Date(g.startsAt).toLocaleString(undefined, {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    });

                    return (
                      <div
                        key={g.eventId}
                        className="p-3.5 rounded-2xl bg-slate-900/90 border border-slate-800 space-y-2.5"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="space-y-0.5">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <span className="font-extrabold text-sm text-white">{g.title}</span>
                              {g.isStreamed && (
                                <span className="px-2 py-0.5 rounded-full bg-rose-500/20 border border-rose-400/60 text-rose-200 text-[9px] font-extrabold inline-flex items-center gap-1">
                                  <Radio className="w-2.5 h-2.5" />
                                  LIVE STREAMED
                                </span>
                              )}
                              {g.recurrence !== 'none' && (
                                <span className="px-2 py-0.5 rounded-full bg-indigo-500/20 border border-indigo-400/50 text-indigo-200 text-[9px] font-bold uppercase">
                                  {g.recurrence}
                                </span>
                              )}
                              {g.bracket && (
                                <span className="px-1.5 py-0.5 rounded bg-violet-500/20 text-violet-200 text-[9px] font-bold">
                                  Bracket B{g.bracket}
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-400">
                              <strong className="text-amber-300">{localTimeStr}</strong> ·{' '}
                              {FORMAT_PRESETS[g.format]?.label ?? g.format} · Host:{' '}
                              <span className="text-slate-200 font-semibold">{g.hostDisplayName}</span> ·{' '}
                              {g.approvalMode === 'host_approved' ? 'Host Approval' : 'First-Come + Waitlist'}
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5">
                            {g.streamUrl && (
                              <a
                                href={g.streamUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="px-2.5 py-1 rounded-lg bg-rose-950/70 hover:bg-rose-900 border border-rose-500/50 text-rose-200 font-bold text-[10px] inline-flex items-center gap-1"
                              >
                                <span>Watch Stream</span>
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                            <a
                              href={`/api/calendar/${encodeURIComponent(g.eventId)}.ics`}
                              download={`${g.eventId}.ics`}
                              className="px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-[10px] inline-flex items-center gap-1"
                            >
                              <Download className="w-3 h-3" />
                              <span>.ICS</span>
                            </a>
                          </div>
                        </div>

                        {g.description && <p className="text-slate-300 text-[11px]">{g.description}</p>}

                        {/* Roster & Waitlist Chips */}
                        <div className="flex flex-wrap items-center gap-1.5 pt-1">
                          <span className="text-[10px] font-bold text-emerald-300 inline-flex items-center gap-1 mr-1">
                            <Users className="w-3 h-3" />
                            {g.confirmedCount}/{g.maxSeats} Confirmed
                            {g.waitlistCount > 0 ? ` · ${g.waitlistCount} Waitlisted` : ''}
                          </span>
                          {g.signups.map((s) => (
                            <span
                              key={s.publicId}
                              className={`px-2 py-0.5 rounded-lg border text-[10px] inline-flex items-center gap-1.5 ${
                                s.status === 'confirmed'
                                  ? 'bg-emerald-950/50 border-emerald-500/40 text-emerald-100'
                                  : s.status === 'waitlist'
                                  ? 'bg-amber-950/50 border-amber-500/40 text-amber-200'
                                  : s.status === 'pending_approval'
                                  ? 'bg-indigo-950/50 border-indigo-400/40 text-indigo-200'
                                  : 'bg-slate-950 border-slate-800 text-slate-500 line-through'
                              }`}
                            >
                              <span
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: s.profileColor }}
                              />
                              <span className="font-semibold">{s.displayName}</span>
                              {s.commanderName && (
                                <span className="text-slate-400">({s.commanderName})</span>
                              )}
                              <span className="uppercase text-[8px] opacity-75">{s.status}</span>
                              {isHost &&
                                s.publicId !== profile?.publicId &&
                                (s.status === 'pending_approval' || s.status === 'waitlist') && (
                                  <>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        await playtableApi.approveScheduledSignup(
                                          g.eventId,
                                          s.publicId,
                                          true
                                        );
                                        refreshCalendar();
                                      }}
                                      className="px-1 rounded bg-emerald-600 text-white font-bold"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        await playtableApi.approveScheduledSignup(
                                          g.eventId,
                                          s.publicId,
                                          false
                                        );
                                        refreshCalendar();
                                      }}
                                      className="px-1 rounded bg-rose-700 text-white font-bold"
                                    >
                                      ✕
                                    </button>
                                  </>
                                )}
                            </span>
                          ))}
                        </div>

                        {/* Sign Up / Withdraw / Launch Controls */}
                        <div className="pt-2 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-2">
                          {mySignup ? (
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-300 font-bold text-[11px]">
                                Your Status: {mySignup.status.toUpperCase()}
                              </span>
                              <button
                                type="button"
                                onClick={async () => {
                                  const res = await playtableApi.withdrawScheduledGame(g.eventId);
                                  if (res.promotedPublicId) {
                                    setStatusMsg('Withdrew seat — next waitlisted player auto-promoted!');
                                  }
                                  refreshCalendar();
                                }}
                                className="px-2.5 py-1 rounded-lg bg-rose-950/80 hover:bg-rose-900 border border-rose-500/50 text-rose-200 font-semibold"
                              >
                                Withdraw Slot
                              </button>
                            </div>
                          ) : (
                            <div className="flex flex-wrap items-center gap-2 flex-1">
                              <input
                                value={commanderInputByEvent[g.eventId] ?? ''}
                                onChange={(e) =>
                                  setCommanderInputByEvent((prev) => ({
                                    ...prev,
                                    [g.eventId]: e.target.value,
                                  }))
                                }
                                placeholder="Optional Commander / Archetype..."
                                className="px-2.5 py-1 rounded-lg bg-slate-950 border border-slate-700 text-[11px] w-48"
                              />
                              {g.isStreamed && (
                                <label className="inline-flex items-center gap-1.5 text-[10px] text-rose-200 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={Boolean(consentByEvent[g.eventId])}
                                    onChange={(e) =>
                                      setConsentByEvent((prev) => ({
                                        ...prev,
                                        [g.eventId]: e.target.checked,
                                      }))
                                    }
                                    className="accent-rose-500"
                                  />
                                  <span>I consent to live broadcast & VOD recording</span>
                                </label>
                              )}
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    const acct = await ensureSignedIn();
                                    if (!acct) return;
                                    await playtableApi.signUpScheduledGame(g.eventId, {
                                      streamConsentAccepted: Boolean(consentByEvent[g.eventId]),
                                      commanderName: commanderInputByEvent[g.eventId]?.trim() || undefined,
                                    });
                                    refreshCalendar();
                                  } catch (err: any) {
                                    setStatusMsg(err.message);
                                  }
                                }}
                                className="px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold"
                              >
                                {g.openSeats > 0 ? 'Claim Seat' : 'Join Waitlist'}
                              </button>
                            </div>
                          )}

                          <div className="flex items-center gap-1.5">
                            {g.launchedTableId && (
                              <button
                                type="button"
                                onClick={() => {
                                  onJoinLaunchedTable(g.launchedTableId!);
                                  onClose();
                                }}
                                className="px-3 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold inline-flex items-center gap-1"
                              >
                                <Play className="w-3 h-3" />
                                <span>Join Live Pod</span>
                              </button>
                            )}
                            {isHost && !g.launchedTableId && (
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    const launched = await playtableApi.launchScheduledGame(g.eventId);
                                    onJoinLaunchedTable(launched.tableId, launched.overlaySecret);
                                    onClose();
                                  } catch (err: any) {
                                    setStatusMsg(err.message);
                                  }
                                }}
                                className="px-3 py-1 rounded-lg bg-violet-600 hover:bg-violet-500 text-white font-extrabold inline-flex items-center gap-1"
                              >
                                <Play className="w-3 h-3" />
                                <span>Launch Pod Table</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleScheduleSubmit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="space-y-1 sm:col-span-2">
                  <span className="text-slate-400 font-semibold">Event Title</span>
                  <input
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white"
                  />
                </label>

                <label className="space-y-1">
                  <span className="text-slate-400 font-semibold">Format</span>
                  <select
                    value={format}
                    onChange={(e) => setFormat(e.target.value as FormatId)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white"
                  >
                    {Object.values(FORMAT_PRESETS).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="space-y-1">
                  <span className="text-slate-400 font-semibold">Seat Limit (1–8) & Bracket</span>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={maxSeats}
                      onChange={(e) => setMaxSeats(Number(e.target.value))}
                      className="px-2.5 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white"
                    >
                      {[2, 3, 4, 5, 6, 7, 8].map((n) => (
                        <option key={n} value={n}>
                          {n} Seats
                        </option>
                      ))}
                    </select>
                    <select
                      value={bracket}
                      onChange={(e) => setBracket(e.target.value)}
                      className="px-2.5 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white"
                    >
                      <option value="">Any Bracket</option>
                      {[1, 2, 3, 4, 5].map((b) => (
                        <option key={b} value={String(b)}>
                          Bracket B{b}
                        </option>
                      ))}
                    </select>
                  </div>
                </label>

                <label className="space-y-1">
                  <span className="text-slate-400 font-semibold">Start Date & Time ({localTz})</span>
                  <input
                    type="datetime-local"
                    required
                    value={startsLocal}
                    onChange={(e) => setStartsLocal(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white"
                  />
                </label>

                <label className="space-y-1">
                  <span className="text-slate-400 font-semibold">Recurrence & Sign-Up Approval</span>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={recurrence}
                      onChange={(e) => setRecurrence(e.target.value as 'none' | 'weekly' | 'biweekly')}
                      className="px-2.5 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white"
                    >
                      <option value="none">One-Time</option>
                      <option value="weekly">Weekly</option>
                      <option value="biweekly">Every 2 Weeks</option>
                    </select>
                    <select
                      value={approvalMode}
                      onChange={(e) =>
                        setApprovalMode(e.target.value as 'first_come' | 'host_approved')
                      }
                      className="px-2.5 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white"
                    >
                      <option value="first_come">First-Come</option>
                      <option value="host_approved">Host Approved</option>
                    </select>
                  </div>
                </label>

                <label className="space-y-1 sm:col-span-2">
                  <span className="text-slate-400 font-semibold">Pod Notes / Rule 0 Expectations</span>
                  <textarea
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-white"
                  />
                </label>

                <div className="sm:col-span-2 p-3 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
                  <label className="flex items-center justify-between cursor-pointer">
                    <span className="font-bold text-rose-200">
                      This game will be broadcast live (requires player stream consent on sign-up)
                    </span>
                    <input
                      type="checkbox"
                      checked={isStreamed}
                      onChange={(e) => setIsStreamed(e.target.checked)}
                      className="accent-rose-500"
                    />
                  </label>
                  {isStreamed && (
                    <input
                      value={streamUrl}
                      onChange={(e) => setStreamUrl(e.target.value)}
                      placeholder="https://twitch.tv/yourchannel or YouTube Live URL"
                      className="w-full px-3 py-1.5 rounded-lg bg-slate-950 border border-slate-700 text-xs"
                    />
                  )}
                </div>
              </div>

              <button
                type="submit"
                className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-sm"
              >
                Publish Scheduled Pod
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// 2. STREAM MODE QR COMPANION PAIRING & OBS OVERLAY SUITE PANEL
// ============================================================================

export function StreamCompanionAndObsSuite({
  view,
  overlaySecret,
  onOverlaySecretChanged,
  onDispatch,
}: {
  view: ClientGameView;
  overlaySecret: string | null;
  onOverlaySecretChanged?: (nextSecret: string) => void;
  onDispatch: (intent: GameIntent) => void;
}) {
  const mySeat = view.viewerSeatId ? view.seats[view.viewerSeatId] : null;
  const [pairInfo, setPairInfo] = useState<{
    code: string;
    pairUrl: string;
    qrSvg: string;
    purpose: 'private_hand' | 'obs_dock' | 'overhead_camera';
  } | null>(null);
  const [obsLayout, setObsLayout] = useState<'landscape' | 'vertical'>('landscape');
  const [obsAudio, setObsAudio] = useState<boolean>(false);
  const [obsPreview, setObsPreview] = useState<boolean>(false);
  const [delayedHandEnabled, setDelayedHandEnabled] = useState<boolean>(false);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [twitchCmdInput, setTwitchCmdInput] = useState<string>('Sol Ring');
  const [twitchReply, setTwitchReply] = useState<string | null>(null);

  const copyVal = (key: string, val: string) => {
    navigator.clipboard?.writeText(val);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  const obsOverlayUrl = overlaySecret
    ? `${getSessionBaseUrl()}?tableId=${encodeURIComponent(
        view.tableId
      )}&overlaySecret=${encodeURIComponent(overlaySecret)}&layout=${obsLayout}&audio=${
        obsAudio ? '1' : '0'
      }${obsPreview ? '&preview=1' : ''}`
    : null;

  const qrDataUri = pairInfo
    ? `data:image/svg+xml;utf8,${encodeURIComponent(pairInfo.qrSvg)}`
    : null;

  return (
    <div className="space-y-3 text-xs">
      <div className="font-bold text-sm text-white flex items-center gap-1.5">
        <Radio className="w-4 h-4 text-rose-400" />
        <span>Stream Mode, QR Companion & OBS Suite</span>
      </div>

      {/* Panic Stream-Hide & Clicked Card Privacy */}
      {mySeat && (
        <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
          <button
            type="button"
            onClick={() => onDispatch({ type: 'TOGGLE_STREAM_MODE', enabled: !mySeat.streamMode })}
            className={`w-full py-2 px-3 rounded-xl font-extrabold flex items-center justify-center gap-1.5 ${
              mySeat.streamMode
                ? 'bg-rose-600 text-white'
                : 'bg-slate-950 hover:bg-slate-800 border border-slate-700 text-slate-200'
            }`}
          >
            <EyeOff className="w-3.5 h-3.5" />
            <span>
              {mySeat.streamMode
                ? 'Stream Mode ON (Hand Stripped Over Wire)'
                : 'Enable Stream Mode (Zero-Leak)'}
            </span>
          </button>

          <p className="text-[10px] text-slate-400 leading-relaxed">
            When active, your hand, scry/tutor cards, private scratchpad, and deck art pool are stripped on
            the server before reaching this broadcast tab.
          </p>
        </div>
      )}

      {/* Companion Phone / OBS Custom Dock QR & Short-Code Pairing */}
      {mySeat && (
        <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-bold text-indigo-300 flex items-center gap-1">
              <QrCode className="w-3.5 h-3.5" />
              <span>Pair Phone / OBS Control Dock</span>
            </span>
          </div>

          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={async () => {
                const res = await playtableApi.createCompanionPairCode(view.tableId, 'private_hand');
                setPairInfo(res);
              }}
              className="py-1.5 px-2 rounded-lg bg-indigo-600/30 hover:bg-indigo-600/50 border border-indigo-400/50 text-indigo-100 font-bold text-[10px]"
            >
              QR: Private Hand Phone
            </button>
            <button
              type="button"
              onClick={async () => {
                const res = await playtableApi.createCompanionPairCode(view.tableId, 'obs_dock');
                setPairInfo(res);
              }}
              className="py-1.5 px-2 rounded-lg bg-violet-600/30 hover:bg-violet-600/50 border border-violet-400/50 text-violet-100 font-bold text-[10px]"
            >
              QR: OBS Control Dock
            </button>
          </div>

          {pairInfo && qrDataUri && (
            <div className="p-2.5 rounded-xl bg-slate-950 border border-indigo-500/40 flex flex-col items-center gap-2 text-center">
              <img
                src={qrDataUri}
                alt={`Pairing code ${pairInfo.code}`}
                className="w-36 h-36 rounded-lg border border-slate-700 bg-white p-1"
              />
              <div>
                <div className="text-[10px] text-slate-400">6-Char Single-Use Pairing Code (5m TTL)</div>
                <div className="font-mono font-black text-base tracking-widest text-amber-300">
                  {pairInfo.code}
                </div>
              </div>
              <button
                type="button"
                onClick={() => copyVal('pair', pairInfo.pairUrl)}
                className="w-full py-1 px-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[10px] font-semibold flex items-center justify-center gap-1"
              >
                {copiedKey === 'pair' ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                <span>{copiedKey === 'pair' ? 'Copied Pairing Link!' : 'Copy Companion Link'}</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* OBS Transparent Display Overlay Builder */}
      {obsOverlayUrl && (
        <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
          <div className="font-bold text-amber-300">OBS Transparent Display Overlay</div>

          <div className="grid grid-cols-2 gap-1.5">
            {(
              [
                ['landscape', '1920×1080 Landscape'],
                ['vertical', '1080×1920 Vertical'],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                onClick={() => setObsLayout(id)}
                className={`py-1 px-2 rounded-lg text-[10px] font-bold border ${
                  obsLayout === id
                    ? 'bg-amber-500 text-slate-950 border-amber-400'
                    : 'bg-slate-950 border-slate-700 text-slate-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="flex items-center justify-between text-[11px] cursor-pointer">
            <span className="text-slate-300">Overlay WebAudio Effects</span>
            <input
              type="checkbox"
              checked={obsAudio}
              onChange={(e) => setObsAudio(e.target.checked)}
              className="accent-amber-500"
            />
          </label>

          <label className="flex items-center justify-between text-[11px] cursor-pointer">
            <span className="text-slate-300">Pre-Stream Test / Framing Banner</span>
            <input
              type="checkbox"
              checked={obsPreview}
              onChange={(e) => setObsPreview(e.target.checked)}
              className="accent-amber-500"
            />
          </label>

          {mySeat && (
            <div className="pt-1.5 border-t border-slate-800 space-y-1.5">
              <label className="flex items-center justify-between text-[11px] cursor-pointer">
                <span className="text-slate-300">
                  Delayed Hand on OBS ({view.settings.spectatorDelaySec}s delay)
                </span>
                <input
                  type="checkbox"
                  checked={delayedHandEnabled}
                  disabled={view.settings.spectatorDelaySec < 15}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    setDelayedHandEnabled(next);
                    await playtableApi.setOverlayDelayedHandReveal(view.tableId, next);
                  }}
                  className="accent-emerald-500"
                />
              </label>
              {view.settings.spectatorDelaySec < 15 && (view.isHost || view.isJudge) && (
                <button
                  type="button"
                  onClick={() =>
                    onDispatch({
                      type: 'UPDATE_TABLE_SETTINGS',
                      patch: { spectatorDelaySec: 30 },
                    })
                  }
                  className="w-full py-1 rounded bg-slate-950 hover:bg-slate-800 border border-slate-700 text-[10px] text-amber-300"
                >
                  Set 30s Broadcast Delay to Unlock Delayed Hand Overlay
                </button>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-1.5 pt-1">
            <button
              type="button"
              onClick={() => copyVal('obs', obsOverlayUrl)}
              className="py-1.5 px-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-extrabold text-[10px] flex items-center justify-center gap-1"
            >
              <Copy className="w-3 h-3" />
              <span>{copiedKey === 'obs' ? 'Copied!' : 'Copy OBS URL'}</span>
            </button>
            <button
              type="button"
              onClick={async () => {
                const rotated = await playtableApi.rotateOverlaySecret(view.tableId);
                onOverlaySecretChanged?.(rotated.overlaySecret);
                setCopiedKey('rotated');
                setTimeout(() => setCopiedKey(null), 1600);
              }}
              className="py-1.5 px-2 rounded-lg bg-rose-950/80 hover:bg-rose-900 border border-rose-500/50 text-rose-200 font-bold text-[10px]"
            >
              {copiedKey === 'rotated' ? 'Secret Rotated!' : 'Rotate Secret'}
            </button>
          </div>
        </div>
      )}

      {/* Twitch Bot (!card, !deck & Poll/Channel Point) Quick Verifier */}
      {overlaySecret && (
        <div className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 space-y-2">
          <div className="font-bold text-violet-300">Twitch Chat `!card` & `!deck` Integration</div>
          <div className="flex gap-1">
            <input
              value={twitchCmdInput}
              onChange={(e) => setTwitchCmdInput(e.target.value)}
              placeholder="Card name for !card..."
              className="flex-1 px-2 py-1 rounded bg-slate-950 border border-slate-700 text-[11px]"
            />
            <button
              type="button"
              onClick={async () => {
                const res = await playtableApi.testTwitchIntegration({
                  tableId: view.tableId,
                  overlaySecret,
                  kind: 'card_lookup',
                  query: twitchCmdInput,
                });
                setTwitchReply(res.reply);
              }}
              className="px-2 py-1 rounded bg-violet-600 hover:bg-violet-500 text-white font-bold text-[10px]"
            >
              !card
            </button>
            <button
              type="button"
              onClick={async () => {
                const res = await playtableApi.testTwitchIntegration({
                  tableId: view.tableId,
                  overlaySecret,
                  kind: 'deck_lookup',
                });
                setTwitchReply(res.reply);
              }}
              className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-[10px]"
            >
              !deck
            </button>
          </div>
          {twitchReply && (
            <div className="p-2 rounded bg-slate-950 border border-slate-800 text-[10px] text-slate-300 leading-snug">
              {twitchReply}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
